import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  CssBaseline,
  Stack,
  alpha,
  Typography,
  Grid,
} from '@mui/material';
import { formatNumber } from '../../../utils/generalFunctions';
import AppNavbar from '../../../components/AppNavbar';
import Header from '../../../components/Header';
import SideMenu from '../../../components/SideMenu';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../dashboard/theme/customizations';
import AppTheme from '../../../shared-theme/AppTheme';
import InversionistasList from './InversionistasList';
import { MetricCard, ACCENTS } from './inversionistasUI';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const InversionistasModule = (props) => {
  const [inversionistas, setInversionistas] = useState([]);
  const [gananciaGeneral, setGananciaGeneral] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const idcompany = localStorage.getItem('idcompany');
  const usuarioId = localStorage.getItem('id');

  const headers = {
    'Content-Type': 'application/json',
    'X-Usuario-ID': usuarioId,
    'X-company-ID': idcompany,
  };

  const fetchInversionistas = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/inversionistas/${idcompany}`,
        { headers }
      );
      if (response.ok) setInversionistas(await response.json());
    } catch (error) {
      console.error('Error fetching inversionistas:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idcompany, usuarioId]);

  const fetchGananciaGeneral = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/empresa/ganancia-general/${idcompany}`,
        { headers }
      );
      if (response.ok) setGananciaGeneral(await response.json());
    } catch (error) {
      console.error('Error fetching ganancia general:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idcompany, usuarioId]);

  useEffect(() => {
    fetchInversionistas();
    fetchGananciaGeneral();
  }, [refreshTrigger, fetchInversionistas, fetchGananciaGeneral]);

  const handleRefresh = () => setRefreshTrigger((prev) => prev + 1);

  const capitalInvertido = inversionistas.reduce(
    (acc, inv) => acc + parseFloat(inv.capital_invertido || 0), 0);
  const rendimientoMensual = inversionistas.reduce(
    (acc, inv) => acc + parseFloat(inv.rendimiento_mensual || 0), 0);
  const activos = inversionistas.filter((inv) => inv.estado === 'activo').length;

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{ alignItems: 'center', mx: 3, pb: 5, mt: { xs: 8, md: 0 } }}
          >
            <Header parent={"Gestión Comercial"} child={"Inversionistas"} />
            <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
              <Box maxWidth="lg" mx="auto">
                <Box mb={3}>
                  <Typography variant="h4" fontWeight={700} letterSpacing="-0.01em" textAlign="left">
                    Inversionistas
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} textAlign="left">
                    Gestiona inversionistas y sus acuerdos de inversión sobre la ganancia general de la empresa.
                  </Typography>
                </Box>

                {/* Tarjetas resumen */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                      label="Inversionistas activos"
                      value={`${activos} / ${inversionistas.length}`}
                      {...ACCENTS.teal}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                      label="Capital invertido"
                      value={formatNumber(capitalInvertido)}
                      {...ACCENTS.green}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                      label="Rendimiento mensual"
                      value={formatNumber(rendimientoMensual)}
                      sub="Comprometido a inversionistas"
                      {...ACCENTS.orange}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                      label="Ganancia general (mes)"
                      value={formatNumber(gananciaGeneral?.ganancia_general || 0)}
                      sub="Intereses + moras cobrados"
                      {...ACCENTS.purple}
                    />
                  </Grid>
                </Grid>

                <InversionistasList onRefresh={handleRefresh} />
              </Box>
            </Container>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
};

export default InversionistasModule;
